import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api, OeeWindowRow } from "../api/rest";
import { useSiteId } from "../hooks/useSiteId";
import { useWarehouseId } from "../hooks/useWarehouseId";
import { AutoGrid } from "../app/layout/AutoGrid";
import styles from "./FleetPage.module.css";
import tabStyles from "./AlertsPage.module.css";
import oeStyles from "./OeePage.module.css";

type Range = "today" | "7d" | "30d";

function rangeWindow(r: Range): { from: Date; to: Date } {
  const to = new Date();
  const from = new Date(to);
  if (r === "today")   from.setHours(0, 0, 0, 0);
  else if (r === "7d") from.setDate(from.getDate() - 7);
  else                 from.setDate(from.getDate() - 30);
  return { from, to };
}

interface AssetOee {
  assetId: string;
  availability: number;
  performance: number;
  quality: number;
  oee: number;
  productive: number;
  exceptions: number;
  runMs: number;
}

function aggregate(rows: OeeWindowRow[]): AssetOee[] {
  const map = new Map<string, { runMs: number; aSumW: number; pSumW: number; qSumW: number; oSumW: number; prod: number; exc: number }>();
  for (const r of rows) {
    const a = map.get(r.asset_id) ?? { runMs: 0, aSumW: 0, pSumW: 0, qSumW: 0, oSumW: 0, prod: 0, exc: 0 };
    const w = r.run_time_ms;
    a.runMs  += w;
    a.aSumW  += r.availability * w;
    a.pSumW  += r.performance  * w;
    a.qSumW  += r.quality      * w;
    a.oSumW  += r.oee          * w;
    a.prod   += r.productive_moves;
    a.exc    += r.exception_moves;
    map.set(r.asset_id, a);
  }
  return Array.from(map.entries())
    .map(([assetId, a]) => ({
      assetId,
      availability: a.runMs ? a.aSumW / a.runMs : 0,
      performance:  a.runMs ? a.pSumW / a.runMs : 0,
      quality:      a.runMs ? a.qSumW / a.runMs : 0,
      oee:          a.runMs ? a.oSumW / a.runMs : 0,
      productive: a.prod,
      exceptions: a.exc,
      runMs: a.runMs,
    }))
    .sort((a, b) => b.oee - a.oee);
}

function fmtPct(x: number) {
  return `${(x * 100).toFixed(1).replace(".", ",")}%`;
}

function StatTile({ label, value, color }: { label: string; value: number; color: string }) {
  const pct = Math.min(1, Math.max(0, value));
  return (
    <div className={oeStyles.tile}>
      <div className={oeStyles.tileLabel}>{label}</div>
      <div className={oeStyles.tileValue}>{fmtPct(value)}</div>
      <div className={oeStyles.barTrack}>
        <div className={oeStyles.barFill} style={{ width: `${pct * 100}%`, background: color }} />
      </div>
    </div>
  );
}

export function OeePage() {
  const SITE_ID = useSiteId();
  const WAREHOUSE_ID = useWarehouseId();
  const { t } = useTranslation();
  const [range, setRange] = useState<Range>("today");

  const { from, to } = useMemo(() => rangeWindow(range), [range]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["oee", SITE_ID, WAREHOUSE_ID, range],
    queryFn: () => api.getOeeRange(SITE_ID, from, to, WAREHOUSE_ID),
    refetchInterval: range === "today" ? 60_000 : false,
    retry: 0,
  });

  // Site-level summary comes from the capability-graded endpoint (#56/#61):
  // absent factors are omitted (never rendered as 0) and the headline is only
  // called OEE when the grading allows it.
  const modular = useQuery({
    queryKey: ["oee-modular", SITE_ID, range],
    queryFn: () => api.getOeeModular(SITE_ID, from, to),
    refetchInterval: range === "today" ? 60_000 : false,
    retry: 0,
  });

  const assets = useMemo(() => (data ? aggregate(data) : []), [data]);

  return (
    <div className={styles.page}>
      <h1 className={styles.h1}>{t("oee.title")}</h1>

      <div className={tabStyles.tabs} role="tablist">
        {(["today", "7d", "30d"] as Range[]).map((r) => (
          <button
            key={r}
            role="tab"
            aria-selected={range === r}
            className={range === r ? tabStyles.tabActive : tabStyles.tab}
            onClick={() => setRange(r)}
          >
            {t(`oee.range.${r}`)}
          </button>
        ))}
      </div>

      {/* Fleet summary — graded, only measured factors rendered */}
      {modular.data && (
        <div className={oeStyles.summary}>
          <AutoGrid min={190}>
            {modular.data.oee !== undefined && (
              <StatTile label={t(`oee.grade.${modular.data.grade}`)} value={modular.data.oee} color="var(--op-blue-500)" />
            )}
            {modular.data.grade === "INDICADORES" && modular.data.composite !== undefined && (
              <StatTile
                label={`${t("oee.composite")} (${modular.data.compositeLabel})`}
                value={modular.data.composite}
                color="var(--op-blue-500)"
              />
            )}
            {modular.data.availability && (
              <StatTile label={t("oee.col.availability")} value={modular.data.availability.value} color="var(--op-success)" />
            )}
            {modular.data.performance && (
              <StatTile label={t("oee.col.performance")} value={modular.data.performance.value} color="var(--op-warn)" />
            )}
            {modular.data.quality && (
              <StatTile label={t("oee.col.quality")} value={modular.data.quality.value} color="var(--op-info)" />
            )}
          </AutoGrid>
          {modular.data.basis.length > 0 && (
            <div className={oeStyles.basis} style={{ marginTop: "var(--space-3)" }}>
              {t("oee.basis")}: {modular.data.basis.join(" · ")}
            </div>
          )}
        </div>
      )}

      {/* Per-asset table */}
      <div className={styles.tableScroll} style={{ marginTop: "var(--space-4)" }}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>{t("oee.col.asset")}</th>
            <th>{t("oee.col.oee")}</th>
            <th>{t("oee.col.availability")}</th>
            <th>{t("oee.col.performance")}</th>
            <th>{t("oee.col.quality")}</th>
            <th>{t("oee.col.productive")}</th>
            <th>{t("oee.col.exceptions")}</th>
          </tr>
        </thead>
        <tbody>
          {isLoading
            ? Array.from({ length: 4 }, (_, i) => (
                <tr key={i}>
                  {[90, 60, 60, 60, 60, 50, 50].map((w, j) => (
                    <td key={j}><span className="skeleton" style={{ width: w, height: 14, display: "block" }} /></td>
                  ))}
                </tr>
              ))
            : isError
              ? <tr><td colSpan={7} className={styles.error}>{t("oee.error")}</td></tr>
              : assets.length === 0
                ? <tr><td colSpan={7} className={styles.empty}>{t("oee.empty")}</td></tr>
                : assets.map((a) => (
                    <tr key={a.assetId}>
                      <td className={styles.mono}>{a.assetId}</td>
                      <td className={styles.mono} style={{ fontWeight: 600 }}>{fmtPct(a.oee)}</td>
                      <td className={styles.mono}>{fmtPct(a.availability)}</td>
                      <td className={styles.mono}>{fmtPct(a.performance)}</td>
                      <td className={styles.mono}>{fmtPct(a.quality)}</td>
                      <td className={styles.mono}>{a.productive}</td>
                      <td className={styles.mono}>{a.exceptions}</td>
                    </tr>
                  ))
          }
        </tbody>
      </table>
      </div>
    </div>
  );
}
